import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, type IconName } from '../Icon/Icon';

import { Root, LabelRow, LabelGroup, Label, Hint, Input } from './TextArea.styles';

export type TextAreaProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Optional small glyph shown before the label. */
  iconName?: IconName;
  /** Visible height in text rows (the control stays vertically resizable). */
  rows?: number;
  /** Trailing hint beside the label, e.g. a character count ("142/160"). */
  hint?: string;
  disabled?: boolean;
  /** Grow/shrink to fit the content (up to a cap, then scroll) instead of a fixed row box — for prompts
   *  whose length varies a lot. */
  autoGrow?: boolean;
};

/** Labelled multiline input — `Field`'s textarea sibling (same label + surface treatment). */
export const TextArea = ({
  label,
  value,
  onChange,
  placeholder,
  rows = 4,
  hint,
  disabled = false,
  autoGrow = false,
  iconName,
}: TextAreaProps): React.ReactElement => {
  const { t } = useTranslation();
  return (
    <Root>
      <LabelRow>
        <LabelGroup>
          {iconName ? <Icon name={iconName} size={15} /> : null}
          <Label text={label} tag="span" />
        </LabelGroup>
        {hint ? <Hint>{hint}</Hint> : null}
      </LabelRow>
      <Input
        value={value}
        rows={rows}
        $autoGrow={autoGrow}
        aria-label={t(label)}
        placeholder={placeholder ? t(placeholder) : undefined}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </Root>
  );
};

export default TextArea;
